import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Icon,
    IconButton,
    Switch,
    Tooltip
} from '@mui/material';
import PropTypes from 'prop-types';
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftInput from "../../../components/SoftInput";
import SoftButton from "../../../components/SoftButton";
import {
    getCameraSnapshotUrl,
    getRestrictedZones,
    saveRestrictedZones
} from "../../../api/branch/restrictedZoneApiClient";

// One colour per zone so overlapping outlines stay tellable apart.
const ZONE_COLORS = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00897b'];
const MIN_POINTS = 3;

const colorFor = (index) => ZONE_COLORS[index % ZONE_COLORS.length];

/**
 * Draws no-go areas on a still frame from the camera.
 *
 * Points are kept normalised to 0..1 — the same units the backend stores and
 * the detector expects — so the overlay needs no scaling maths and survives the
 * dialog being resized or the camera resolution changing.
 */
const RestrictedZoneDialog = ({open, onClose, camera, onSaved}) => {
    const [zones, setZones] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [snapshotUrl, setSnapshotUrl] = useState(null);
    const [snapshotError, setSnapshotError] = useState(null);
    const [loadingSnapshot, setLoadingSnapshot] = useState(false);
    const [loadingZones, setLoadingZones] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [draggingPoint, setDraggingPoint] = useState(null);

    const svgRef = useRef(null);
    // Held in a ref as well so the cleanup effect can revoke the current URL
    // without re-running (and re-fetching) every time the snapshot changes.
    const snapshotUrlRef = useRef(null);

    const cameraId = camera?.cameraId;

    const releaseSnapshot = useCallback(() => {
        if (snapshotUrlRef.current) {
            URL.revokeObjectURL(snapshotUrlRef.current);
            snapshotUrlRef.current = null;
        }
    }, []);

    const loadSnapshot = useCallback(async () => {
        if (!cameraId) return;
        setLoadingSnapshot(true);
        setSnapshotError(null);
        try {
            const url = await getCameraSnapshotUrl(cameraId);
            releaseSnapshot();
            snapshotUrlRef.current = url;
            setSnapshotUrl(url);
        } catch (err) {
            // A missing frame must not block editing: an admin can still adjust
            // an existing outline against the grid if the camera is offline.
            setSnapshotError(err.message);
            setSnapshotUrl(null);
        } finally {
            setLoadingSnapshot(false);
        }
    }, [cameraId, releaseSnapshot]);

    useEffect(() => {
        if (!open || !cameraId) return;
        setError(null);
        setSelectedIndex(0);
        setLoadingZones(true);
        getRestrictedZones(cameraId)
            .then((data) => setZones((data || []).map((zone) => ({
                zoneId: zone.zoneId,
                name: zone.name,
                polygon: zone.polygon || [],
                enabled: zone.enabled,
            }))))
            .catch((err) => setError(err.message || 'Could not load restricted areas'))
            .finally(() => setLoadingZones(false));
        loadSnapshot();
    }, [open, cameraId, loadSnapshot]);

    useEffect(() => releaseSnapshot, [releaseSnapshot]);

    const selectedZone = zones[selectedIndex];

    const updateSelected = (changes) => {
        setZones((current) => current.map((zone, index) =>
            index === selectedIndex ? {...zone, ...changes} : zone));
    };

    const pointFromEvent = (event) => {
        const rect = svgRef.current.getBoundingClientRect();
        // Clamped: a drag that leaves the frame should stick to the edge rather
        // than produce a coordinate the API will reject.
        const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
        return {x, y};
    };

    const handleSurfaceClick = (event) => {
        if (!selectedZone || draggingPoint !== null) return;
        updateSelected({polygon: [...selectedZone.polygon, pointFromEvent(event)]});
    };

    const handlePointerMove = (event) => {
        if (draggingPoint === null || !selectedZone) return;
        const moved = pointFromEvent(event);
        updateSelected({
            polygon: selectedZone.polygon.map((point, index) => index === draggingPoint ? moved : point),
        });
    };

    const handleUndo = () => {
        if (!selectedZone || selectedZone.polygon.length === 0) return;
        updateSelected({polygon: selectedZone.polygon.slice(0, -1)});
    };

    const handleAddZone = () => {
        setZones((current) => {
            const next = [...current, {zoneId: null, name: `Area ${current.length + 1}`, polygon: [], enabled: true}];
            setSelectedIndex(next.length - 1);
            return next;
        });
    };

    const handleRemoveZone = (index) => {
        setZones((current) => current.filter((_, i) => i !== index));
        setSelectedIndex((current) => (current >= index && current > 0 ? current - 1 : current));
    };

    const handleSave = async () => {
        const incomplete = zones.find((zone) => zone.polygon.length < MIN_POINTS);
        if (incomplete) {
            setError(`"${incomplete.name}" needs at least ${MIN_POINTS} points. Click on the frame to add them.`);
            return;
        }
        const unnamed = zones.find((zone) => !zone.name || !zone.name.trim());
        if (unnamed) {
            setError('Every area needs a name — it is what the alert is titled with.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await saveRestrictedZones(cameraId, zones);
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const renderZoneOutline = (zone, index) => {
        if (zone.polygon.length === 0) return null;
        const isSelected = index === selectedIndex;
        const color = colorFor(index);
        const points = zone.polygon.map((p) => `${p.x},${p.y}`).join(' ');

        return (
            <g key={zone.zoneId ?? `new-${index}`} opacity={isSelected ? 1 : 0.35}>
                <polygon
                    points={points}
                    fill={color}
                    fillOpacity={zone.enabled ? 0.22 : 0.08}
                    stroke={color}
                    strokeWidth={isSelected ? 0.005 : 0.003}
                    strokeDasharray={zone.enabled ? undefined : '0.02 0.015'}
                    vectorEffect="non-scaling-stroke"
                />
                {isSelected && zone.polygon.map((point, pointIndex) => (
                    <circle
                        key={pointIndex}
                        cx={point.x}
                        cy={point.y}
                        r={0.012}
                        fill="#fff"
                        stroke={color}
                        strokeWidth={0.004}
                        style={{cursor: 'grab'}}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setDraggingPoint(pointIndex);
                        }}
                    />
                ))}
            </g>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Restricted Areas — Camera {camera?.channel}
                <SoftTypography variant="caption" color="text" sx={{display: 'block', fontSize: '0.75rem'}}>
                    Alert branch staff when a child is detected inside one of these areas.
                </SoftTypography>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <SoftBox
                            sx={{
                                position: 'relative',
                                width: '100%',
                                aspectRatio: '16 / 9',
                                backgroundColor: 'grey.900',
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            {snapshotUrl && (
                                <img
                                    src={snapshotUrl}
                                    alt="Camera frame"
                                    style={{width: '100%', height: '100%', objectFit: 'fill', display: 'block'}}
                                />
                            )}
                            {loadingSnapshot && (
                                <SoftBox sx={{
                                    position: 'absolute', inset: 0, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', color: 'white',
                                }}>
                                    <CircularProgress color="inherit" size={28}/>
                                </SoftBox>
                            )}
                            {!loadingSnapshot && !snapshotUrl && (
                                <SoftBox sx={{
                                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center', color: 'white', p: 2,
                                }}>
                                    <Icon fontSize="large">videocam_off</Icon>
                                    <SoftTypography variant="caption" color="white" textAlign="center">
                                        {snapshotError || 'No frame available'}
                                    </SoftTypography>
                                </SoftBox>
                            )}
                            <svg
                                ref={svgRef}
                                viewBox="0 0 1 1"
                                preserveAspectRatio="none"
                                style={{
                                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                                    cursor: selectedZone ? 'crosshair' : 'default',
                                }}
                                onClick={handleSurfaceClick}
                                onMouseMove={handlePointerMove}
                                onMouseUp={() => setDraggingPoint(null)}
                                onMouseLeave={() => setDraggingPoint(null)}
                            >
                                {zones.map(renderZoneOutline)}
                            </svg>
                        </SoftBox>

                        <SoftBox display="flex" alignItems="center" gap={1} mt={1.5}>
                            <SoftButton size="small" variant="outlined" color="secondary"
                                        onClick={loadSnapshot} disabled={loadingSnapshot}>
                                <Icon sx={{mr: 0.5}}>refresh</Icon>
                                Refresh frame
                            </SoftButton>
                            <SoftButton size="small" variant="outlined" color="secondary"
                                        onClick={handleUndo}
                                        disabled={!selectedZone || selectedZone.polygon.length === 0}>
                                <Icon sx={{mr: 0.5}}>undo</Icon>
                                Undo point
                            </SoftButton>
                            <SoftTypography variant="caption" color="text">
                                {selectedZone
                                    ? `Click the frame to add points to "${selectedZone.name}" — drag a point to move it.`
                                    : 'Add an area to start drawing.'}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <SoftTypography variant="button" fontWeight="bold">
                                Areas ({zones.length})
                            </SoftTypography>
                            <SoftButton size="small" variant="gradient" color="primary" onClick={handleAddZone}>
                                <Icon sx={{mr: 0.5}}>add</Icon>
                                Add area
                            </SoftButton>
                        </SoftBox>

                        {loadingZones && <CircularProgress size={20}/>}

                        {!loadingZones && zones.length === 0 && (
                            <SoftTypography variant="caption" color="text">
                                No restricted areas yet. Add one, then outline it on the frame — a window, a
                                door, or the main gate.
                            </SoftTypography>
                        )}

                        {zones.map((zone, index) => (
                            <SoftBox
                                key={zone.zoneId ?? `new-${index}`}
                                onClick={() => setSelectedIndex(index)}
                                sx={{
                                    p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: index === selectedIndex ? colorFor(index) : 'grey.300',
                                    backgroundColor: index === selectedIndex ? 'grey.100' : 'transparent',
                                }}
                            >
                                <SoftBox display="flex" alignItems="center" gap={1}>
                                    <SoftBox sx={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        backgroundColor: colorFor(index), flexShrink: 0,
                                    }}/>
                                    <SoftInput
                                        size="small"
                                        value={zone.name}
                                        placeholder="Area name"
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setZones((current) => current.map((z, i) =>
                                            i === index ? {...z, name: e.target.value} : z))}
                                    />
                                    <Tooltip title={zone.enabled ? 'Monitoring on' : 'Monitoring off'}>
                                        <Switch
                                            size="small"
                                            checked={zone.enabled}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => setZones((current) => current.map((z, i) =>
                                                i === index ? {...z, enabled: e.target.checked} : z))}
                                        />
                                    </Tooltip>
                                    <IconButton size="small" onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveZone(index);
                                    }}>
                                        <Icon fontSize="small" color="error">delete</Icon>
                                    </IconButton>
                                </SoftBox>
                                <SoftTypography variant="caption" color={
                                    zone.polygon.length < MIN_POINTS ? 'error' : 'text'
                                }>
                                    {zone.polygon.length < MIN_POINTS
                                        ? `${zone.polygon.length}/${MIN_POINTS} points — needs more`
                                        : `${zone.polygon.length} points`}
                                </SoftTypography>
                            </SoftBox>
                        ))}
                    </Grid>
                </Grid>

                {error && (
                    <SoftBox mt={2}>
                        <SoftTypography variant="caption" color="error">{error}</SoftTypography>
                    </SoftBox>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <SoftButton variant="gradient" color="primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save areas'}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
};

RestrictedZoneDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    camera: PropTypes.object,
    onSaved: PropTypes.func,
};

export default RestrictedZoneDialog;
